import React, { useState, useMemo, useEffect } from 'react';
import { X, DollarSign, Clock, CheckCircle, FileText, User, Car, Calendar, Hash, Info, Building, Wrench, Save, Truck, Tag, ClipboardList, XCircle, AlertTriangle, Receipt, ListChecks } from 'lucide-react';
import { solicitudesService } from '../../../../services/api';
import { useApp } from '../../../../context/AppContext';
import AlertModal from './AlertModal';
import SeguimientoOT from './SeguimientoOT';

// Color por fase real (estados_solicitud.fase), no por codigo a mano: asi
// cualquiera de los 34 estados reales cae en un color razonable sin tener
// que listarlos todos.
const FASE_COLOR = {
  solicitud: '#F59E0B',
  compra: '#F59E0B',
  transito: '#3B82F6',
  entrega: '#3B82F6',
  cobro: '#EC4899',
  cerrado: '#10B981'
};
const FASE_LABEL = {
  solicitud: 'Solicitud', compra: 'Compra', transito: 'Tránsito',
  entrega: 'Entrega', cobro: 'Cobro', cerrado: 'Cerrado'
};

const RejectModal = ({ onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Rechazar Orden</h3>
              <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo del Rechazo <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              rows={4}
              placeholder="Describa la razón por la cual se rechaza esta orden de trabajo..."
              autoFocus
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={() => onConfirm(reason)}
              disabled={!reason.trim() || loading}
              className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed font-medium text-sm flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <XCircle size={16} />
              )}
              Confirmar Rechazo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, valueClass = 'text-gray-800' }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-5 h-5 mt-0.5 flex-shrink-0 flex items-center justify-center">
      <Icon size={15} className="text-gray-400" />
    </div>
    <div className="min-w-0">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <p className={`text-sm font-medium ${valueClass} break-words`}>{value || 'N/A'}</p>
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, iconColor = 'text-blue-600', bgColor = 'bg-blue-50', borderColor = 'border-blue-200', children, className = '' }) => (
  <div className={`${bgColor} border ${borderColor} rounded-xl p-4 ${className}`}>
    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3" style={{ color: iconColor.replace('text-', '') }}>
      <Icon size={16} className={iconColor} />
      {title}
    </h4>
    {children}
  </div>
);

const OrdenTrabajoDetailModal = ({ orden, onClose, onUpdated }) => {
  const [updating, setUpdating] = useState(false);
  const [costoManoObra, setCostoManoObra] = useState(Number(orden.costo_mano_obra) || 0);
  const [guardandoCosto, setGuardandoCosto] = useState(false);
  const [costoGuardado, setCostoGuardado] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [alert, setAlert] = useState({ open: false, title: '', message: '', type: 'info' });
  const { handleViewChange, setSolicitudFilterId } = useApp();

  const [estadosReales, setEstadosReales] = useState([]);
  const [nuevoEstado, setNuevoEstado] = useState(orden.estado || '');
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [estadoGuardado, setEstadoGuardado] = useState(false);
  const [facturacion, setFacturacion] = useState(null);
  const [cargandoFacturacion, setCargandoFacturacion] = useState(true);

  const showAlert = (title, message, type = 'info') => {
    setAlert({ open: true, title, message, type });
  };

  const handleCloseAlert = () => setAlert(prev => ({ ...prev, open: false }));

  const vehiculoPlaca = orden.vehiculo_placa || 'N/A';
  const vehiculoId = orden.vehiculo_id || 'N/A';
  const nombreVehiculo = orden.vehiculo_nombre || null;
  const empresaVehiculo = orden.empresa_nombre || null;

  const totalArticulos = useMemo(() => {
    if (!orden.articulos || orden.articulos.length === 0) return 0;
    return orden.articulos.reduce((sum, art) => {
      const precio = Number(art.precio_venta) || 0;
      return sum + precio * Number(art.cantidad);
    }, 0);
  }, [orden.articulos]);

  const totalGeneral = useMemo(() => {
    return totalArticulos + Number(costoManoObra);
  }, [totalArticulos, costoManoObra]);

  const isRechazado = orden.estado === 'rechazado_cliente' || orden.estado === 'rechazado_gaman';

  useEffect(() => {
    solicitudesService.getEstadosReales()
      .then(res => setEstadosReales(res.data || []))
      .catch(err => console.error('Error cargando estados reales:', err));
  }, []);

  useEffect(() => {
    setCargandoFacturacion(true);
    solicitudesService.getFacturacion(orden.id)
      .then(res => setFacturacion(res.data))
      .catch(err => console.error('Error cargando trazabilidad de cobro:', err))
      .finally(() => setCargandoFacturacion(false));
  }, [orden.id]);

  const estadoActualInfo = useMemo(
    () => estadosReales.find(e => e.codigo === orden.estado),
    [estadosReales, orden.estado]
  );

  const estadosPorFase = useMemo(() => {
    const grupos = {};
    for (const e of estadosReales) {
      if (!grupos[e.fase]) grupos[e.fase] = [];
      grupos[e.fase].push(e);
    }
    return grupos;
  }, [estadosReales]);

  const handleGuardarCostoManoObra = async () => {
    setGuardandoCosto(true);
    setCostoGuardado(false);
    try {
      await solicitudesService.update(orden.id, { costo_mano_obra: Number(costoManoObra) || 0 });
      setCostoGuardado(true);
      setTimeout(() => setCostoGuardado(false), 3000);
      if (onUpdated) onUpdated();
    } catch (error) {
      console.error('Error guardando costo mano de obra:', error);
      showAlert('Error', error.message || 'Error al guardar el costo de mano de obra', 'error');
    } finally {
      setGuardandoCosto(false);
    }
  };

  const handleRechazar = async (motivo) => {
    setUpdating(true);
    try {
      await solicitudesService.update(orden.id, { estado: 'rechazado_cliente', motivo_rechazo: motivo });
      setShowRejectModal(false);
      if (onUpdated) onUpdated();
      onClose();
    } catch (error) {
      console.error('Error al rechazar orden:', error);
      showAlert('Error al rechazar', error.message || 'Error al rechazar la orden', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleGuardarEstado = async () => {
    if (!nuevoEstado || nuevoEstado === orden.estado) return;
    setGuardandoEstado(true);
    setEstadoGuardado(false);
    try {
      await solicitudesService.update(orden.id, { estado: nuevoEstado });
      setEstadoGuardado(true);
      setTimeout(() => setEstadoGuardado(false), 3000);
      const res = await solicitudesService.getFacturacion(orden.id);
      setFacturacion(res.data);
      if (onUpdated) onUpdated();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      showAlert('Error', error.message || 'Error al actualizar el estado', 'error');
    } finally {
      setGuardandoEstado(false);
    }
  };

  const handleViewInGestion = () => {
    setSolicitudFilterId(orden.id);
    handleViewChange('solicitudes-gestion');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-blue-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 truncate">Detalle de Orden</h2>
                <p className="text-xs text-gray-500 truncate">{orden.folio || `#${orden.id}`}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2">
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="p-4 sm:p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex-1">{orden.nombre_solicitud}</h3>
              <span
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white text-center whitespace-nowrap self-start ${isRechazado ? 'animate-pulse' : ''}`}
                style={{ backgroundColor: FASE_COLOR[estadoActualInfo?.fase] || '#6B7280' }}
              >
                {estadoActualInfo?.nombre || orden.estado}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SectionCard title="Información General" icon={ClipboardList} iconColor="text-blue-600" bgColor="bg-blue-50/60" borderColor="border-blue-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoRow icon={Hash} label="Folio" value={orden.folio || `#${orden.id}`} />
                  <InfoRow icon={Calendar} label="Fecha de Creación" value={orden.fecha_creacion ? new Date(orden.fecha_creacion).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} />
                  <InfoRow icon={Tag} label="Servicio" value={orden.servicio_nombre || orden.id_servicio || 'N/A'} />
                  <InfoRow icon={User} label="Cliente ID" value={orden.id_cliente || 'N/A'} />
                  {orden.cliente_nombre && (
                    <InfoRow icon={User} label="Cliente" value={orden.cliente_nombre} valueClass="text-blue-700 font-semibold" />
                  )}
                  {orden.usuario_asignado_nombre && (
                    <InfoRow icon={User} label="Asignado a" value={orden.usuario_asignado_nombre} />
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Vehículo" icon={Car} iconColor="text-amber-600" bgColor="bg-amber-50/60" borderColor="border-amber-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoRow icon={Hash} label="ID Vehículo" value={vehiculoId} />
                  <InfoRow icon={Truck} label="Placa" value={vehiculoPlaca} valueClass="text-amber-700 font-bold uppercase" />
                  {orden.vehiculo_modelo && (
                    <InfoRow icon={Car} label="Modelo" value={orden.vehiculo_modelo} />
                  )}
                  {nombreVehiculo && (
                    <InfoRow icon={Tag} label="Nombre" value={nombreVehiculo} />
                  )}
                  {empresaVehiculo && (
                    <InfoRow icon={Building} label="Empresa" value={empresaVehiculo} />
                  )}
                </div>
              </SectionCard>
            </div>

            {isRechazado && orden.motivo_rechazo && (
              <SectionCard title="Motivo de Rechazo" icon={XCircle} iconColor="text-red-600" bgColor="bg-red-50" borderColor="border-red-200">
                <p className="text-sm text-red-800 whitespace-pre-wrap">{orden.motivo_rechazo}</p>
              </SectionCard>
            )}

            {orden.requiere_anticipo && (
              <SectionCard title="Anticipo" icon={DollarSign} iconColor="text-purple-600" bgColor="bg-purple-50" borderColor="border-purple-200">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Monto USD</p>
                    <p className="text-lg font-bold text-purple-900">${Number(orden.anticipo_monto_usd).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Tasa de Cambio</p>
                    <p className="text-lg font-bold text-purple-900">{orden.anticipo_tasa_cambio} Bs/USD</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Total Bs.</p>
                    <p className="text-lg font-bold text-purple-900">Bs. {(Number(orden.anticipo_monto_usd) * Number(orden.anticipo_tasa_cambio)).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Fecha de Pago</p>
                    <p className="text-lg font-bold text-purple-900">{orden.anticipo_fecha ? new Date(orden.anticipo_fecha).toLocaleDateString() : 'Pendiente'}</p>
                  </div>
                </div>
                {!orden.anticipo_fecha && (
                  <p className="text-xs text-purple-600 flex items-center gap-1 mt-2">
                    <Info size={12} />
                    Anticipo pendiente de pago — la tasa quedará congelada al momento del pago
                  </p>
                )}
              </SectionCard>
            )}

            {orden.descripcion && (
              <SectionCard title="Descripción" icon={FileText} iconColor="text-gray-600" bgColor="bg-gray-50" borderColor="border-gray-200">
                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {orden.descripcion}
                </div>
              </SectionCard>
            )}

            {orden.articulos && orden.articulos.length > 0 && (
              <SectionCard title={`Repuestos / Artículos (${orden.articulos.length})`} icon={Wrench} iconColor="text-emerald-600" bgColor="bg-white" borderColor="border-emerald-200">
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-emerald-50 border-b border-emerald-200">
                        <th className="text-left px-3 py-2.5 font-semibold text-emerald-800 text-xs uppercase tracking-wider">Artículo</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-emerald-800 text-xs uppercase tracking-wider">Cant.</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-emerald-800 text-xs uppercase tracking-wider">Precio Unit.</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-emerald-800 text-xs uppercase tracking-wider">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orden.articulos.map((art, idx) => {
                        const precio = Number(art.precio_venta) || 0;
                        const subtotal = precio * Number(art.cantidad);
                        return (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-emerald-50/40 transition-colors">
                            <td className="px-3 py-2.5">
                              <span className="font-medium text-gray-800">{art.observacion || art.nombre || art.articulo_nombre || `Artículo #${art.id_articulo}`}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-600 font-medium">{art.cantidad}</td>
                            <td className="px-3 py-2.5 text-right text-gray-600">${precio.toFixed(2)}</td>
                            <td className="px-3 py-2.5 text-right font-semibold text-gray-800">${subtotal.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-emerald-50/80 font-semibold">
                        <td colSpan="3" className="px-3 py-3 text-right text-emerald-800 text-sm">Total Artículos</td>
                        <td className="px-3 py-3 text-right text-emerald-900 text-base">${totalArticulos.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </SectionCard>
            )}

            {!isRechazado && (
              <SectionCard title="Costo por Mano de Obra" icon={Wrench} iconColor="text-amber-600" bgColor="bg-amber-50/60" borderColor="border-amber-200">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-amber-700 mb-1.5">Monto en USD</label>
                    <div className="relative max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={costoManoObra}
                        onChange={(e) => setCostoManoObra(parseFloat(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      onClick={handleGuardarCostoManoObra}
                      disabled={guardandoCosto}
                      className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 transition-all text-sm font-medium shadow-sm"
                    >
                      {guardandoCosto ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save size={15} />
                      )}
                      {costoGuardado ? 'Guardado' : 'Guardar Costo'}
                    </button>
                    {costoGuardado && (
                      <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle size={14} /> Registrado
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Resumen Financiero</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <span className="text-gray-600">Artículos: <span className="font-semibold text-gray-800">${totalArticulos.toFixed(2)}</span></span>
                      <span className="text-gray-600">Mano de Obra: <span className="font-semibold text-amber-700">${Number(costoManoObra).toFixed(2)}</span></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-700">Total General:</span>
                    <span className="text-xl sm:text-2xl font-bold text-blue-900">${totalGeneral.toFixed(2)}</span>
                  </div>
                </div>
              </SectionCard>
            )}

            {!isRechazado && (
              <SectionCard title="Cambiar Estado" icon={Clock} iconColor="text-indigo-600" bgColor="bg-indigo-50/60" borderColor="border-indigo-100">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-indigo-700 mb-1.5">Nuevo estado</label>
                    <select
                      value={nuevoEstado}
                      onChange={(e) => setNuevoEstado(e.target.value)}
                      className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      {Object.entries(estadosPorFase).map(([fase, lista]) => (
                        <optgroup key={fase} label={FASE_LABEL[fase] || fase}>
                          {lista.map(e => <option key={e.codigo} value={e.codigo}>{e.nombre}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      onClick={handleGuardarEstado}
                      disabled={guardandoEstado || !nuevoEstado || nuevoEstado === orden.estado}
                      className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium shadow-sm"
                    >
                      {guardandoEstado ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save size={15} />
                      )}
                      {estadoGuardado ? 'Guardado' : 'Guardar Estado'}
                    </button>
                    {estadoGuardado && (
                      <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle size={14} /> Actualizado
                      </span>
                    )}
                  </div>
                </div>
              </SectionCard>
            )}

            <SectionCard title="Trazabilidad de Cobro" icon={Receipt} iconColor="text-emerald-700" bgColor="bg-emerald-50/60" borderColor="border-emerald-200">
              {cargandoFacturacion ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : facturacion?.factura_id ? (
                <div className="space-y-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
                    Con factura
                  </span>
                  <p className="text-sm text-gray-800">
                    Factura <strong>{facturacion.numero_factura}</strong> — estado{' '}
                    <strong>{facturacion.factura_estado}</strong> — {facturacion.factura_moneda === 'VES' ? 'Bs.' : '$'}
                    {Number(facturacion.factura_total).toFixed(2)}
                  </p>
                  {facturacion.fecha_pago && (
                    <p className="text-xs text-gray-500">Pagada el {new Date(facturacion.fecha_pago).toLocaleDateString('es-VE')}</p>
                  )}
                </div>
              ) : facturacion?.cotizacion_id ? (
                <div className="space-y-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                    Sin factura formal todavía
                  </span>
                  <p className="text-sm text-gray-800">
                    Cotización <strong>{facturacion.cotizacion_numero}</strong> ({facturacion.cotizacion_estado}
                    {facturacion.pasada_a_factura ? ', pasada a facturación' : ''})
                  </p>
                  <p className="text-xs text-gray-500">
                    Cobro registrado por estado: <strong>{estadoActualInfo?.nombre || orden.estado}</strong>
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-600">
                    Sin factura
                  </span>
                  <p className="text-sm text-gray-700">
                    Esta orden no está ligada a ninguna cotización/factura. El cobro se rastrea solo por el estado:{' '}
                    <strong>{estadoActualInfo?.nombre || orden.estado}</strong>
                  </p>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Seguimiento del Trabajo" icon={ListChecks} iconColor="text-blue-700" bgColor="bg-white" borderColor="border-blue-200">
              <SeguimientoOT solicitudId={orden.id} />
            </SectionCard>

            {!isRechazado && (
              <div className="border-t border-gray-200 pt-3">
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={updating}
                  className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 transition-all text-sm font-medium shadow-sm"
                >
                  <XCircle size={15} />
                  Rechazar Orden
                </button>
              </div>
            )}

            <div className="border-t border-gray-200 pt-5 flex flex-wrap gap-3">
              <button
                onClick={handleViewInGestion}
                className="px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 transition-all text-sm font-medium shadow-sm"
              >
                <FileText size={15} />
                Ver en Gestión de Solicitudes
              </button>
            </div>
          </div>
        </div>
      </div>

      {showRejectModal && (
        <RejectModal
          onConfirm={handleRechazar}
          onCancel={() => setShowRejectModal(false)}
          loading={updating}
        />
      )}

      <AlertModal
        open={alert.open}
        onClose={handleCloseAlert}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default OrdenTrabajoDetailModal;
